import type { CSSProperties } from 'react';
import type { Device } from '../types';

type WarrantyDevice = Pick<Device, 'warrantyStartAt' | 'warrantyEndAt'>;

export type DeviceWarrantyStatus = {
  active: boolean;
  hasWarrantyDates: boolean;
  isReplacementPeriod: boolean;
  startAt: Date | null;
  endAt: Date | null;
};

const parseDateOnlyParts = (value: string): Date | null => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const parseDateValue = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const dateOnly = parseDateOnlyParts(value);
  if (dateOnly) {
    return dateOnly;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (value?: string | Date | null): Date | null => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return null;
  }
  const normalized = new Date(parsed);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const endOfDay = (value?: string | Date | null): Date | null => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return null;
  }
  const normalized = new Date(parsed);
  normalized.setHours(23, 59, 59, 999);
  return normalized;
};

const addOneMonth = (value: Date): Date => {
  const next = new Date(value);
  next.setMonth(next.getMonth() + 1);
  return next;
};

export const getDeviceWarrantyStatus = (
  device?: WarrantyDevice | null,
  referenceDateInput?: string | Date | null
): DeviceWarrantyStatus => {
  const startAt = startOfDay(device?.warrantyStartAt || null);
  const endAt = endOfDay(device?.warrantyEndAt || null);
  const hasWarrantyDates = Boolean(startAt || endAt);
  const referenceDate = parseDateValue(referenceDateInput) || new Date();

  if (!hasWarrantyDates) {
    return { active: false, hasWarrantyDates: false, isReplacementPeriod: false, startAt, endAt };
  }

  const afterStart = startAt ? referenceDate >= startAt : true;
  const beforeEnd = endAt ? referenceDate <= endAt : false;
  const active = afterStart && beforeEnd;
  const replacementPeriodEnd = startAt ? addOneMonth(startAt) : null;
  const isReplacementPeriod = Boolean(active && startAt && replacementPeriodEnd && referenceDate < replacementPeriodEnd);

  return {
    active,
    hasWarrantyDates,
    isReplacementPeriod,
    startAt,
    endAt
  };
};

export const getDeviceWarrantyChipLabel = (status?: DeviceWarrantyStatus | null): string => {
  if (!status?.active) {
    return 'No warranty';
  }
  return 'Warranty';
};

export const getDeviceWarrantySummaryLabel = (status?: DeviceWarrantyStatus | null): string => {
  if (!status?.active) {
    return 'No active warranty';
  }
  return status.isReplacementPeriod ? 'In warranty - first month' : 'In warranty';
};

export const getDeviceWarrantyChipStyle = (status?: DeviceWarrantyStatus | null): CSSProperties => ({
  alignItems: 'center',
  background: status?.isReplacementPeriod ? '#fff4d6' : '#e6f0f7',
  border: `1px solid ${status?.isReplacementPeriod ? '#f3c96b' : '#b8d3e5'}`,
  borderRadius: '999px',
  color: status?.isReplacementPeriod ? '#8a611e' : '#1f5b72',
  display: 'inline-flex',
  fontSize: '0.72rem',
  fontWeight: 800,
  gap: '0.25rem',
  letterSpacing: '0.01em',
  lineHeight: 1,
  padding: '0.26rem 0.48rem',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
});

export const getDeviceWarrantyFirstMonthChipStyle = (): CSSProperties => ({
  alignItems: 'center',
  background: '#fff4d6',
  border: '1px solid #f3c96b',
  borderRadius: '999px',
  color: '#8a611e',
  display: 'inline-flex',
  fontSize: '0.72rem',
  fontWeight: 800,
  gap: '0.25rem',
  letterSpacing: '0.01em',
  lineHeight: 1,
  padding: '0.26rem 0.48rem',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
});
